<script setup lang="ts">
import RoomCard from '../RoomCard.vue';
import { useData } from '../../_composables/useData';
import type HotelRoomType from '@itinerary/hotel/HotelRoomType';

const { loadingRoom, prices } = defineProps<{
    loadingRoom?: boolean;
    prices: HotelRoomType[];
}>();

const emit = defineEmits<{
    research: [];
    'update:intoView': [intoView: boolean];
}>();

const HotelSchedule = defineAsyncComponent(() => import('./HotelSchedule.vue'));

const roomsRef = ref<HTMLDivElement>();

const { hotelPackage, title } = useData();

const isOneSelectableRoomSelected = computed(
    () =>
        hotelPackage.value.selectedPricesWithPriceClass?.[0]?.[1]
            ?.onlyThisTypeSelectable
);

const onscroll = () => {
    let value = false;
    if (roomsRef.value?.clientHeight) {
        const currentOffset = window.scrollY + window.innerHeight + 48;
        value =
            currentOffset > roomsRef.value.offsetTop &&
            currentOffset <
                roomsRef.value.offsetTop +
                    roomsRef.value.clientHeight +
                    window.innerHeight;
    }
    emit('update:intoView', value);
};
const scrollToTop = () => {
    nextTick(() => {
        if (isOneSelectableRoomSelected.value && roomsRef.value) {
            const top =
                roomsRef.value.getBoundingClientRect().top +
                window.scrollY -
                48;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    });
};

onMounted(() => window.addEventListener('scroll', onscroll));
onBeforeUnmount(() => window.removeEventListener('scroll', onscroll));
</script>
<template>
    <div ref="roomsRef" class="rooms" v-bind="$attrs">
        <mr-typography
            v-if="prices.length"
            size="x-large"
            weight="700"
            type="h2"
            class="title"
        >
            اتاق‌های موجود در {{ title }}
        </mr-typography>
        <template v-if="loadingRoom">
            <div class="rooms-loading loading-skeleton" />
            <div class="rooms-loading loading-skeleton" />
            <div class="rooms-loading loading-skeleton" />
        </template>
        <template v-else-if="prices.length">
            <TransitionGroup>
                <RoomCard
                    v-for="price in prices"
                    :key="price.id"
                    :item="price"
                    :nights="hotelPackage.nights"
                    @select="scrollToTop"
                />
            </TransitionGroup>
        </template>
        <div v-else>
            <div class="no-room-find">
                <mr-typography
                    size="x-large"
                    weight="700"
                    type="h2"
                    class="title"
                >
                    در بازه زمانی انتخابی شما، ظرفیت اتاق‌های این هتل تکمیل است!
                </mr-typography>
                <HotelSchedule :pkg="hotelPackage" />
            </div>
        </div>
    </div>
</template>
<style scoped lang="scss">
.no-room-find {
    background-color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    border-radius: 8px;
    @include shadow();
    padding: 16px;
    margin-bottom: 16px;
    @include desktop-only {
        overflow: scroll;
    }
}

.title {
    margin-bottom: 8px;
    margin-top: 0;
    @include mobile-only {
        margin-bottom: 0;
    }
}
.rooms {
    .rooms-loading {
        height: 164px;
        @include shadow();
        width: 100%;
        @include mobile-only {
            height: 214px;
        }
    }
}
</style>
